import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { X, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helpers
function fmtDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfWeek(d) {
  const day = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
}
function dayToKey(d) {
  return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][(d.getDay() + 6) % 7];
}

const WEEKDAYS_FULL = ['måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag', 'söndag'];

// Cute & encouraging messages randomized per empty day
const MESSAGES = [
  (day) => `Hey! ${day} är HELT tom 🥳 — passa på att boka in något roligt med dig själv!`,
  (day) => `${day} har inget bokat ✨ Perfekt för en spontan paus eller mys-stund 💛`,
  (day) => `Inget alls på ${day}? 🌸 Ta en lugn dag, eller boka in något du längtar efter!`,
  (day) => `${day} ligger öppen som en tom anteckningsbok 📖 — vad känns roligt just nu?`,
  (day) => `Tom dag = möjlighet 💫 ${day} är din helt och hållet`,
];

export default function EmptyDayBubble({ pet, calendarTab = 'calendar' }) {
  const [emptyDay, setEmptyDay] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const v = localStorage.getItem('empty_day_dismissed');
      const parsed = v ? JSON.parse(v) : {};
      // Auto-clean expired dismissals (older than 7 days)
      const now = Date.now();
      const fresh = {};
      Object.entries(parsed).forEach(([k, ts]) => {
        if (now - ts < 7 * 86400000) fresh[k] = ts;
      });
      return fresh;
    } catch { return {}; }
  });

  // Load week's data and find a fully-empty future day in this week
  const checkEmptyDay = useCallback(async () => {
    try {
      const today = new Date();
      const ws = startOfWeek(today);
      const we = new Date(ws); we.setDate(ws.getDate() + 6);
      const start = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate(), 0, 0, 0).toISOString();
      const end = new Date(we.getFullYear(), we.getMonth(), we.getDate(), 23, 59, 59).toISOString();
      const [tasks, schedule, calendar] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/schedule`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/calendar/events`, { withCredentials: true, params: { from_date: start, to_date: end } }).catch(() => ({ data: { items: [] } })),
      ]);
      const cal = calendar.data?.items || [];
      const sch = Array.isArray(schedule.data) ? schedule.data : [];
      const tks = Array.isArray(tasks.data) ? tasks.data : [];

      // Walk forward from today (incl) to end of week
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + dayOffset);
        if (d > we) break;
        const key = fmtDateKey(d);
        if (dismissed[key]) continue;
        const wd = dayToKey(d);
        const isWeekend = wd === 'sat' || wd === 'sun';

        // Calendar events for this day
        const hasCal = cal.some((e) => e.occurrence_date === key);
        // Schedule items for this weekday
        const hasSch = sch.some((s) => (s.days || []).some((k) => k === wd || k === 'everyday' || (isWeekend ? k === 'weekends' : k === 'weekdays')));
        // Tasks due this day
        const hasTask = tks.some((t) => !t.completed && t.due_date && fmtDateKey(new Date(t.due_date)) === key);

        if (!hasCal && !hasSch && !hasTask) {
          setEmptyDay({ date: d, key, label: WEEKDAYS_FULL[(d.getDay() + 6) % 7], isToday: dayOffset === 0 });
          return;
        }
      }
      setEmptyDay(null);
    } catch {
      // Silent fail — bubble is non-critical
    }
  }, [dismissed]);

  useEffect(() => { checkEmptyDay(); }, [checkEmptyDay]);
  // Re-check every 5 min and on tab focus
  useEffect(() => {
    const id = setInterval(checkEmptyDay, 5 * 60 * 1000);
    const onFocus = () => checkEmptyDay();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [checkEmptyDay]);

  const message = useMemo(() => {
    if (!emptyDay) return null;
    const dayLabel = emptyDay.isToday ? 'idag' : emptyDay.label;
    const idx = (emptyDay.date.getDate() + emptyDay.date.getMonth()) % MESSAGES.length;
    return MESSAGES[idx](dayLabel);
  }, [emptyDay]);

  const dismiss = () => {
    if (!emptyDay) return;
    const next = { ...dismissed, [emptyDay.key]: Date.now() };
    try { localStorage.setItem('empty_day_dismissed', JSON.stringify(next)); } catch { /* storage full */ }
    setDismissed(next);
    setEmptyDay(null);
  };

  if (!emptyDay || !message) return null;

  // Render as a floating bubble bottom-right
  return (
    <div
      className="fixed bottom-4 right-4 z-40 max-w-[280px] sm:max-w-[320px] animate-bounce-in"
      data-testid="empty-day-bubble"
    >
      <div className="relative neo-card bg-gradient-to-br from-[#FFE4EC] via-[#FFF0F6] to-[#F5EEFF] p-3 pr-7">
        <button
          onClick={dismiss}
          className="absolute top-1 right-1 p-1 opacity-50 hover:opacity-100"
          data-testid="empty-day-dismiss"
          aria-label="Stäng"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-2">
          {pet?.image ? (
            <img src={pet.image} alt={pet.name || 'Husdjur'} className="w-10 h-10 rounded-full object-cover shrink-0 border-2 border-[#D06892]" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#FFC8DD] flex items-center justify-center text-xl shrink-0 border-2 border-[#D06892]">
              🐾
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase opacity-60 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> {pet?.name || 'Ditt husdjur'} säger
            </p>
            <p className="text-xs leading-snug mt-1" data-testid="empty-day-message">{message}</p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes bounce-in {
          0% { opacity: 0; transform: scale(0.5) translateY(20px); }
          60% { opacity: 1; transform: scale(1.05) translateY(-4px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-bounce-in { animation: bounce-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
      `}</style>
    </div>
  );
}
